import React, { Dispatch, useState } from 'react';
import {
    Dialog,
    DialogActions,
    DialogContent,
    Divider,
    FormControl,
    FormControlLabel,
    FormLabel,
    Radio,
    RadioGroup,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
    DocumentsStateDialogMode,
    hideDocumentsStateDialog,
    updateDocumentsActivation,
} from '../../../redux/project/documents/sliceProjectDocumentsActivation';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import store, { IAppReduxStore } from '../../../redux/store';
import Documents from './_parts/Documents';
import { ContentLayout } from './_parts/ContentLayout';
import CtaButton from './_parts/CtaButton';
import { headingFontBold, labelFontSemiBold, vars } from '../../../Theme';
import { useEnqueueSnackbar } from '../../../hooks/useEnqueueSnackbar';
import { ToastSeverity } from '../Toast/Toast';
import DialogHeader from './_parts/DialogHeader';
import { removeProjectDocumentsInfoItem } from '../../../redux/project/documents/sliceProjectDocuments';
import { fetchViewDataAndDocuments } from '../../../util/ProjectUtils';

const config = {
    options: {
        activate: true,
        deactivate: false,
    },

    [DocumentsStateDialogMode.Testmode]: {
        title: 'titleTestmode',
        descriptionField: false,
    },
    [DocumentsStateDialogMode.Parking]: {
        title: 'titleParking',
        descriptionField: true,
    },
    [DocumentsStateDialogMode.Stopping]: {
        title: 'titleStopping',
        descriptionField: true,
    },
};

const formLabelSx = {
    ...headingFontBold,
    color: vars.black,
};

export default () => {
    const { t } = useTranslation('translations', { keyPrefix: 'documentsStateDialog' });
    const dispatch: Dispatch<unknown> = useDispatch();
    const enqueueSnackbar = useEnqueueSnackbar();
    const params = useParams();
    const { replicaId } = params;
    const { data: documents, loading: loadingDocuments } = useSelector(
        (store: IAppReduxStore) => store.projectDocuments.info,
    );
    const { isDialogOpen, mode } = useSelector(
        (_store: IAppReduxStore) => _store.projectDocumentsActivation,
    );
    const [selection, setSelection] = useState<string>(Object.keys(config.options)[0]);
    const [description, setDescription] = useState<string>('');

    const onSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSelection(event.target.value);
    };

    const removeDocument = (universalId: string) => {
        dispatch(removeProjectDocumentsInfoItem({ universalId }));
    };

    const onClose = () => {
        dispatch(hideDocumentsStateDialog());
    };

    const onConfirm = async () => {
        onClose();
        const result = await store.dispatch(
            updateDocumentsActivation({
                mode,
                replicaId,
                documentIds: documents.map(x => x.UniversalID),
                status: config.options[selection],
                description: config[mode].descriptionField ? description : undefined,
            }),
        );
        const succeed = result.meta.requestStatus === 'fulfilled';
        enqueueSnackbar(
            t(`${result.meta.requestStatus}${DocumentsStateDialogMode[mode]}`, {
                count: documents.length,
            }),
            {
                severity: succeed ? ToastSeverity.Success : ToastSeverity.Error,
            },
        );
        succeed && fetchViewDataAndDocuments(params);
    };

    return (
        <Dialog open={isDialogOpen}>
            <DialogHeader dialogTitle={t(config[mode].title)} onClose={onClose} />
            <DialogContent sx={{ pt: 0, overflowX: 'hidden' }}>
                <ContentLayout
                    loading={loadingDocuments}
                    documentSelection={
                        <Documents documents={documents} removeDocument={removeDocument} />
                    }
                    main={
                        <Stack spacing='12px'>
                            {config[mode].descriptionField && (
                                <FormControl required>
                                    <FormLabel sx={formLabelSx}>{t('description')}</FormLabel>
                                    <TextField
                                        size='medium'
                                        onChange={ev => setDescription(ev.target.value)}
                                        value={description}
                                    />
                                </FormControl>
                            )}
                            <FormControl required>
                                <FormLabel sx={formLabelSx}>{t('choice')}</FormLabel>
                                <RadioGroup row value={selection} onChange={onSelect}>
                                    {Object.keys(config.options).map(option => (
                                        <FormControlLabel
                                            key={option}
                                            value={option}
                                            control={<Radio />}
                                            label={
                                                <Typography sx={{ fontFamily: labelFontSemiBold }}>
                                                    {t(option)}
                                                </Typography>
                                            }
                                        />
                                    ))}
                                </RadioGroup>
                            </FormControl>
                        </Stack>
                    }
                />
            </DialogContent>
            <Divider />
            <DialogActions sx={{ paddingRight: '24px' }}>
                <CtaButton text={t('cancel')} variant='text' onClick={onClose} />
                <CtaButton
                    disabled={!documents.length || (config[mode].descriptionField && !description)}
                    text={t('confirm')}
                    variant='contained'
                    onClick={onConfirm}
                />
            </DialogActions>
        </Dialog>
    );
};
